/**
 * Layout protegido: tudo o que vive dentro do shell.
 *
 * A guarda e um `beforeLoad` **do layout**, e nao de cada tela: registrada uma vez, ela vale
 * para toda rota interna que existir daqui em diante, inclusive as que ainda nao foram
 * escritas. Repetida tela a tela, a proxima nasceria desprotegida e nada falharia.
 *
 * `ensureQueryData` e nao `fetchQuery`: quem acabou de entrar ja tem a sessao no cache (a
 * mutacao de login a gravou) e nao deve pagar um `GET /sessoes/atual` a mais so para
 * atravessar a guarda.
 *
 * **`revalidateIfStale: true`.** Sem ele, `ensureQueryData` devolve o que estiver em cache e
 * **nunca** refaz a chamada, por mais velho que o dado seja: um usuario desativado no meio do
 * expediente continuaria com o shell inteiro montado ate alguma mutacao falhar, e "quem sou
 * eu" ficaria congelado no que valia no login. Com ele, a navegacao continua instantanea — o
 * cache responde na hora — e a revalidacao acontece em segundo plano, dentro do `staleTime`
 * de 30 segundos de `criarClienteDeConsultas`.
 *
 * **So `401` vira redirecionamento.** Sem cookie, `GET /sessoes/atual` responde `401`, e isso
 * significa "entre" — nao "algo quebrou". Qualquer outra falha (um `500` do gateway, DNS
 * caido, o navegador offline) **sobe** para o tratamento de erro do roteador: mandar essa
 * pessoa para a tela de entrada seria mentir duas vezes, porque la a senha certa tambem vai
 * falhar e ela vai concluir que esqueceu a senha. Sessao apenas **expirada** nem chega aqui:
 * `chamar` renova sozinho no `401` (`src/api/cliente.ts`), e so quando a renovacao tambem e
 * recusada e que o erro sobe.
 */

import { createRoute, redirect, useLocation } from '@tanstack/react-router'

import { ErroDaApi } from '@/api/erros'
import { consultaDaSessao } from '@/modulos/identidade/sessao'
import { ModalDoNegocio } from '@/modulos/negocios/ModalDoNegocio'
import { RegiaoDeAvisos } from '@/modulos/negocios/avisos'
import {
  ehEnderecoDeNegocio,
  useFecharNegocioDaBusca,
  validarBuscaDoModal,
} from '@/modulos/negocios/navegacao-do-negocio'
import { Shell } from '@/shell/Shell'
import { rotaRaiz } from './raiz'

/**
 * O layout: o shell e, **por cima** dele, o modal do Negócio quando a busca traz `?negocio=`
 * (11.4). O modal é aberto de qualquer tela (Funil, Hoje, …) acrescentando o parâmetro e
 * mascarando a barra como `/negocios/<id>` — a tela de origem continua montada embaixo, com o
 * estado dela. Ver `modulos/negocios/navegacao-do-negocio.tsx`.
 */
function LayoutDaAplicacao() {
  const { negocio } = rotaDaAplicacao.useSearch()
  const fechar = useFecharNegocioDaBusca()
  /* No endereço real de um Negócio (link colado) o modal é o da rota: não se empilha um segundo. */
  const noEnderecoDoNegocio = useLocation({
    select: (local) => ehEnderecoDeNegocio(local.pathname),
  })
  return (
    <>
      <Shell />
      {negocio === undefined || noEnderecoDoNegocio ? null : (
        <ModalDoNegocio key={negocio} negocioId={negocio} aoFechar={fechar} />
      )}
      {/* O aviso que sobrevive ao modal (11.5): "Negócio entregue…" depois que ele fecha. */}
      <RegiaoDeAvisos ativa={negocio !== undefined || noEnderecoDoNegocio} />
    </>
  )
}

export const rotaDaAplicacao = createRoute({
  getParentRoute: () => rotaRaiz,
  id: 'aplicacao',
  beforeLoad: async ({ context }) => {
    try {
      await context.clienteDeConsultas.ensureQueryData({
        ...consultaDaSessao,
        revalidateIfStale: true,
      })
    } catch (falha) {
      if (falha instanceof ErroDaApi && falha.status === 401) {
        throw redirect({ to: '/entrar' })
      }
      throw falha
    }
  },
  /** `?negocio=<id>`: o Negócio aberto em modal sobre a tela interna (11.4). */
  validateSearch: validarBuscaDoModal,
  component: LayoutDaAplicacao,
})
